import { signal, WritableSignal } from '@angular/core';
import { ConsentGroupName, ConsentServiceName } from 'libs/shared/src/models';

export abstract class BaseScript {
  // Abstract - Properties & Signals
  // The name of the script, used for logging and display purposes
  public abstract name: string;
  // The corresponding category inside the consent management
  public abstract consentCategory: ConsentGroupName;
  // The corresponding service inside the consent management
  public abstract consentService?: ConsentServiceName;

  // Abstract - Methods
  // The URL of the script, used for loading the script
  public abstract getScriptUrl(): string;
  // Identifier for the script element, used for querying the DOM
  public abstract getScriptId(): string;
  // Attributes for the script element, used for loading the script
  public abstract getScriptAttributes(): Record<string, string>;
  // This is a callback function that is called before the script is created
  public beforeCreate?: () => Promise<void> | void;
  // This is a callback function that is called after the script is created
  public afterCreate?: () => Promise<void> | void = () => {
    this.isLoaded.set(true);
  };

  // Properties & Signals
  /**
   * Indicates whether the script has been loaded
   * (needs to be set manually or is called inside afterCreate)
   */
  public isLoaded: WritableSignal<boolean> = signal(false);

  // Methods
  /**
   * Load the script element from the dom
   * @returns The script element if it exists, otherwise null
   */
  public getScriptElement(): HTMLScriptElement | undefined {
    return (document.getElementById(this.getScriptId()) ?? document.querySelector(`script[src="${this.getScriptUrl()}"]`)) as
      | HTMLScriptElement
      | undefined;
  }

  public async loadScript(): Promise<void> {
    // Check if the script is already loaded
    if (this.getScriptElement()) {
      console.debug(`${this.name} script is already loaded.`);
      this.isLoaded.set(true);
      return;
    }

    // Create a new script element
    const script = document.createElement('script');
    script.id = this.getScriptId();
    script.src = this.getScriptUrl();
    // Set the type and data attributes for consent management
    script.type = 'text/plain';
    script.dataset['category'] = this.consentCategory;
    script.dataset['service'] = this.consentService ?? '';

    // Set attributes for the script element
    const attributes = this.getScriptAttributes();
    for (const [key, value] of Object.entries(attributes)) {
      script.setAttribute(key, value);
    }

    script.onerror = () => {
      console.error(`Error loading ${this.name} script.`);
    };

    await this.beforeCreate?.();

    // Append the script to the document head
    document.head.appendChild(script);

    setTimeout(async () => {
      await this.afterCreate?.();
    }, 100);
  }
}
